import {
    type ActionDiagnosticReference,
    matchesActionDiagnostic,
    useDocumentSupportDiagnostics,
} from '@/context/DocumentSupportDiagnosticsContext';
import type { IActionDecision } from '@models/documentSupport/ActionDiagnostics';

/** Действие (или группа действий) под индикатором: ключ либо тип и название, вид, объект и место — или готовые ссылки. */
export interface IActionDiagnosticTarget {
    actionKey?: string;
    actionType?: string;
    displayName?: string;
    kind?: ActionDiagnosticReference['kind'];
    objectId?: string;
    location?: string;
    references?: ActionDiagnosticReference[];
}

/** Ссылки на действия индикатора; без ключа (или типа и названия) и вида — пусто. */
export const getActionDiagnosticReferences = ({
    actionKey,
    actionType,
    displayName,
    kind,
    objectId,
    location,
    references,
}: IActionDiagnosticTarget): ActionDiagnosticReference[] => {
    const hasIdentity = Boolean(actionKey || (actionType && displayName));
    return (
        references ??
        (hasIdentity && kind ? [{ key: actionKey, actionType, displayName, kind, objectId, location }] : [])
    );
};

export const hasPassedAccessibility = (action: IActionDecision): boolean => {
    const checks = action.checks.filter((check) => check.stage === 'accessibility');
    if (checks.length === 0) return action.executable;
    return checks.every((check) => check.result === 'passed' || check.result === 'notApplicable');
};

/**
 * Решения диагностики по действиям индикатора. Пусто — индикатора нет: режим диагностики выключен, снимка ещё нет или
 * действий в нём нет. По нему же решает место под индикаторы в кнопке действия (`ActionDiagnosticSlot`).
 */
export const useActionDiagnosticDecisions = (references: ActionDiagnosticReference[]): IActionDecision[] => {
    const { enabled, snapshot } = useDocumentSupportDiagnostics();
    if (!enabled || !snapshot) return [];
    return references
        .map((reference) => snapshot.actions.find((action) => matchesActionDiagnostic(action, reference)))
        .filter((action) => action !== undefined);
};
